
import axiosService from './axios-service';
import {
    BulkPullPolicyStateItem,
    Customer,
    DistributePolicyStateMap,
    ExternalApiClientId,
    ExternalApiSecret,
    ServiceDto,
    SidMetadata,
    ExternalIpsByServiceId
} from '../dtos/customer.dto';
import {PolicyMode} from '../utils/enums';
import {BulkPolicyWithApplyDto, ConnectionDataMap, DistributePolicyDto} from '../dtos/policy-bulk-update.dto';
import {SubscriptionDto, SubscriptionType} from '@src/dtos/user-subscription.dto';
import publicAxios from './public-axios';

class CustomerService {

    public getExternalIpsByCustomerId = async (customerId: number): Promise<ExternalIpsByServiceId[]> => {
        const res = await axiosService.get(`/api/customers/customer/${customerId}/get-external-ips`);
        return res?.data;
    };

    public getAllCustomers = async (): Promise<Customer[]> => {
        const res = await axiosService.get(`/api/customers/customer/all`);
        return res?.data;
    };

    public getAllCustomerServices = async (customerId: number): Promise<ServiceDto[]> => {
        const res = await axiosService.get(`/api/firewall/customer/${customerId}/services`);
        return res?.data;
    };

    public getServiceAvailability = async (customerId: number, serviceId: number): Promise<boolean> => {
        const res = await axiosService.get(`/api/firewall/customer/${customerId}/service/${serviceId}/service-availability`);
        return res?.data;
    };

    public getServiceInfo = async (customerId: number, serviceId: number): Promise<SidMetadata> => {
        const res = await axiosService.get(`/api/firewall/customer/${customerId}/service/${serviceId}/service-info`);
        return res?.data;
    };

    public updateConnectionName = async (customerId: number, serviceId: number, updatedServiceName?: string): Promise<ConnectionDataMap> => {
        const res = await axiosService.put(`/api/firewall/customer/${customerId}/service/${serviceId}/service-name`, {
            updatedServiceName
        });
        return res?.data;
    };

    public updateConnectionDetails = async (customerId: number,
                                            serviceId: number,
                                            policyExternalId: string,
                                            connectionName?: string,
                                            policyName?: string,
                                            policyComments?: string): Promise<ConnectionDataMap> => {
        const res = await axiosService.put(`/api/firewall/customer/${customerId}/service/${serviceId}/update-service-data`, {
            externalId: policyExternalId,
            versionName: policyName,
            comments: policyComments,
            updateServiceNameDto : {
                updatedServiceName: connectionName
            }
        });
        return res?.data;
    };

    public getConnectionData = async (customerId: number): Promise<any> => {
        const res = await axiosService.get(`/api/firewall/customer/${customerId}/connections-data`);
        return res?.data;
    };

    public setServiceNameAndPolicyMode =  async (customerId: number, serviceId: number,
                                                updatedServiceName: string, policyMode: PolicyMode): Promise<ConnectionDataMap> => {
        const payload = {
            updatePolicyModeDto: {
                policyMode: policyMode,
            },
            updateServiceNameDto: {
                updatedServiceName: updatedServiceName
            }
        };

        const res = await axiosService.post(`/api/firewall/customer/${customerId}/service/${serviceId}/policy-mode-service-name/update`, payload);
        return res?.data;
    };

    public bulkQuickPolicy = async (customerId: number, dto: BulkPolicyWithApplyDto): Promise<ConnectionDataMap> => {
        const res = await axiosService.post(`/api/firewall/customer/${customerId}/quick-policy-generator`, dto);
        return res?.data;
    };

    public bulkCustomPolicy = async (customerId: number, dto: DistributePolicyDto): Promise<ConnectionDataMap> => {
        const res = await axiosService.post(`/api/firewall/customer/${customerId}/custom-policy-generator`, dto);
        return res?.data;
    };

    public bulkSmartPolicy = async (customerId: number, dto: DistributePolicyDto): Promise<ConnectionDataMap> => {
        const res = await axiosService.post(`/api/firewall/customer/${customerId}/smart-policy-generator`, dto);
        return res?.data;
    };

    public bulkPullPolicy = async (customerId: number, serviceId: number, dto: BulkPolicyWithApplyDto): Promise<ConnectionDataMap> => {
        const res = await axiosService.post(`/api/firewall/customer/${customerId}/service/${serviceId}/bulk-pull-policy`, dto);
        return res?.data;
    };

    public bulkDrafts = async (customerId: number, dto: DistributePolicyDto): Promise<ConnectionDataMap> => {
        const res = await axiosService.post(`/api/firewall/customer/${customerId}/bulk-drafts`, dto);
        return res?.data;
    };

    public rollbackPolicyMode = async (customerId: number, destinationServiceIdList: number[]): Promise<ConnectionDataMap> => {
        const res = await axiosService.delete(`/api/firewall/customer/${customerId}/rollback-policy`,
            { destinationServiceIdList });
        return res?.data;
    };

    public getAllCustomerServicesDistributePolicyStateByList = async (customerId: number, serviceId: number, destinationServiceIdList: number[]):
        Promise<DistributePolicyStateMap> => {
        const res = await axiosService.post(
            `/api/firewall/customer/${customerId}/service/${serviceId}/distribute-policy-state?compareActiveVersion=true&isSchedule=false`, {
            destinationServiceIdList
        });
        return res?.data;
    };

    public getBulkPullPolicyStateMap = async (customerId: number, destinationServiceIdList: number[]): Promise<BulkPullPolicyStateItem[]> => {
        const res = await axiosService.post(`/api/firewall/customer/${customerId}/bulk-pull-policy-state`, {
            destinationServiceIdList
        });
        return res?.data;
    };

    public getExternalApiClientId = async (customerId: number): Promise<ExternalApiClientId> => {
        const res = await axiosService.get(`/api/customers/external_api/client_id/${customerId}`);
        return res?.data;
    };

    public createApiSecret = async (customerId: number): Promise<ExternalApiSecret> => {
        const res = await axiosService.post(`/api/customers/external_api/create_api_credentials/${customerId}`);
        return res?.data;
    };

    public resetApiSecret = async (customerId: number): Promise<ExternalApiSecret> => {
        const res = await axiosService.post(`/api/customers/external_api/reset_api_credentials/${customerId}`);
        return res?.data;
    };

    public getCustomerEligibleForExecutiveSummary = async (customerId: number): Promise<boolean> => {
        const res = await axiosService.get(`/api/firewall/customer/${customerId}/eligibility/executive-summary`);
        return res?.data;
    };

    public getReportSubscription = async (customerId: number, subscriptionType: SubscriptionType): Promise<SubscriptionDto> => {
        const res = await axiosService.get(`/api/customers/customer/${customerId}/subscription/${subscriptionType}`);
        return res?.data;
    };

    public updateReportSubscription = async (customerId: number, requestBody: SubscriptionDto): Promise<SubscriptionDto> => {
        const res = await axiosService.put(`/api/customers/customer/${customerId}/subscription`, requestBody);
        return res?.data;
    };

    public unsubscribe = async (token: string): Promise<void> => {
        await publicAxios.post('/api/customers/unsubscribe', { token });
    };
}

const customerService = new CustomerService();
export default customerService;
